import { useContext, useEffect, useMemo, useState } from 'react';
import { bookingState } from '../client-state/booking-state-context';
import useCancellationPolicy from '../../../Hooks/useCancellationPolicy';
import useLocale from '../../../Hooks/use-locale';
import { isWarrantyAppliedActionTypes } from '../../../redusers/is-warranty-applied-reducer';
import useBookingShop from '../server-data/use-booking-shop';
import { graphqlSdk } from '../../../Utils/graphql-client';
import CancellationProtection from '../../../ui-components/cancellation-protection';
import useTypeSafeLocalize from '../../../Hooks/use-type-safe-localize';
import { ErrorLocalization, LocalizationCategory } from '../../../localizations/types';

const CancelationProtectionWithData = () => {
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const { languageAsEnum } = useLocale();
  const localize = useTypeSafeLocalize();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const { fullCancellationPolicy, hoursBeforeSailOut } = useCancellationPolicy({
    referenceDate: state.booking.date,
  });

  const cutOff = useMemo(() => {
    return (
      fullCancellationPolicy?.options?.warrantyPurchaseCutoff ??
      fullCancellationPolicy?.cancellationWarranty?.date ??
      fullCancellationPolicy?.cancellationWarranty?.startTime ??
      0
    );
  }, [fullCancellationPolicy]);

  const priceModel = useMemo(() => {
    if (!state.booking.priceModelId) return undefined;

    return shop?.priceModels.find(
      (priceModel) => priceModel.id === state.booking.priceModelId
    );
  }, [shop?.priceModels, state.booking.priceModelId]);

  // Get the price of the warranty
  const warrantyPrice = useMemo(() => {
    let price = priceModel?.cancellationWarranty?.price;

    if (price && priceModel?.cancellationWarranty?.per === 'boat') {
      price *= state.booking.boatCount;
    }

    return price;
  }, [priceModel, state.booking.boatCount]);

  // If date and time changes and the hours to sail out are less than the cutoff, the warranty should be removed
  useEffect(() => {
    const removeWarranty = async () => {
      if (state.booking.id) {
        try {
          setLoading(true);
          await graphqlSdk.removeCancellationWarranty({
            bookingId: state.booking.id,
          });
          setLoading(false);

          dispatch({ type: isWarrantyAppliedActionTypes.SET_IS_APPLIED, payload: false });
        } catch (error) {
          setLoading(false);
          setErrorMessage(
            localize(LocalizationCategory.error, ErrorLocalization.UnknownError)
          );
        }
      } else {
        dispatch({ type: isWarrantyAppliedActionTypes.SET_IS_APPLIED, payload: false });
      }

      setErrorMessage(
        `${localize(
          LocalizationCategory.error,
          ErrorLocalization.WarrantyWasRemoved1
        )} ${cutOff} ${localize(
          LocalizationCategory.error,
          ErrorLocalization.WarrantyWasRemoved2
        )}`
      );
    };

    if (
      ((hoursBeforeSailOut !== null && hoursBeforeSailOut < cutOff) || !warrantyPrice) &&
      state.isWarrantyApplied
    ) {
      removeWarranty();
    }
    // `localize` is omitted from the dependency array on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hoursBeforeSailOut,
    cutOff,
    state.booking.id,
    state.isWarrantyApplied,
    dispatch,
    warrantyPrice,
  ]);

  // Set info message based on the availability of the warranty
  useEffect(() => {
    // Cutoff is less than hours to sail out
    if (
      !state.isWarrantyApplied &&
      hoursBeforeSailOut !== null &&
      hoursBeforeSailOut < cutOff
    ) {
      return setInfoMessage(
        `${localize(
          LocalizationCategory.error,
          ErrorLocalization.WarrantyCannotBeAdded1
        )} ${cutOff} ${localize(
          LocalizationCategory.error,
          ErrorLocalization.WarrantyCannotBeAdded2
        )}`
      );
    }

    if (!state.isWarrantyApplied && state.booking.date && !warrantyPrice) {
      return setInfoMessage(
        localize(LocalizationCategory.error, ErrorLocalization.WarrantyInvalidDateAndTime)
      );
    }

    return setInfoMessage('');
    // `localize` is omitted from the dependency array on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cutOff,
    hoursBeforeSailOut,
    state.booking.date,
    state.isWarrantyApplied,
    warrantyPrice,
  ]);

  // Check if the add warranty button should be disabled
  const isDisabled = useMemo(() => {
    if (warrantyPrice && !state.isWarrantyApplied && hoursBeforeSailOut) {
      if (hoursBeforeSailOut >= cutOff) {
        // Reset error message as the customer can now add the warranty
        setErrorMessage('');
        return false;
      }
    }

    return true;
  }, [hoursBeforeSailOut, state.isWarrantyApplied, cutOff, warrantyPrice]);

  const addCancellationWarranty = async () => {
    // Check for disabled here so we can show the info message on button click
    // the add to cart button will only be visually disabled if isDisabled is true
    if (isDisabled) {
      if (!state.booking.date) {
        return setInfoMessage(
          localize(
            LocalizationCategory.error,
            ErrorLocalization.WarrantyMissingDateAndTime
          )
        );
      }

      return;
    }

    setErrorMessage('');

    if (state.booking.id) {
      try {
        setLoading(true);
        await graphqlSdk.booking_addCancellationWarranty({
          bookingId: state.booking.id,
        });
        setLoading(false);

        dispatch({ type: isWarrantyAppliedActionTypes.SET_IS_APPLIED, payload: true });
      } catch (error) {
        setLoading(false);
        setErrorMessage(
          localize(LocalizationCategory.error, ErrorLocalization.UnknownError)
        );
      }
    } else {
      dispatch({ type: isWarrantyAppliedActionTypes.SET_IS_APPLIED, payload: true });
    }
  };

  if (
    fullCancellationPolicy?.cancellationWarranty &&
    (warrantyPrice || shop?.warrantyFromPrice)
  ) {
    return (
      <CancellationProtection
        options={fullCancellationPolicy?.options}
        limit={cutOff}
        price={warrantyPrice}
        basePrice={priceModel?.cancellationWarranty?.price}
        fromPrice={shop?.warrantyFromPrice}
        per={priceModel?.cancellationWarranty?.per ?? ''}
        boatCount={state.booking.boatCount}
        link={shop?.bookingLocalizations?.warrantyLink?.[languageAsEnum]}
        onAdd={addCancellationWarranty}
        loading={loading}
        errorMessage={errorMessage}
        infoMessage={infoMessage}
        isVisuallyDisabled={isDisabled}
        isApplied={state.isWarrantyApplied}
      />
    );
  }

  return null;
};

export default CancelationProtectionWithData;
